import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import type { LostReason, SalesStage } from '../../../api';
import { defaultClientMessageForStage } from '../../../lib/crmStageNotify';
import { SALES_STAGE_META } from './crmLabels';

type Props = {
  orderNumber: string;
  stage: SalesStage;
  hasTelegram?: boolean;
  clientLabel?: string;
  lostReason?: LostReason;
  saving?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (opts: { notifyClient: boolean; clientMessage: string }) => void;
};

export function CrmStageNotifySheet({
  orderNumber,
  stage,
  hasTelegram = true,
  clientLabel,
  lostReason,
  saving = false,
  error = null,
  onCancel,
  onConfirm,
}: Props) {
  const [notifyClient, setNotifyClient] = useState(hasTelegram);
  const [comment, setComment] = useState('');
  const stageLabel = SALES_STAGE_META[stage]?.label ?? stage;
  const preview = useMemo(
    () => defaultClientMessageForStage(orderNumber, stage, { lostReason, managerComment: comment }),
    [orderNumber, stage, lostReason, comment],
  );

  return (
    <div
      className="fixed inset-0 z-[95] flex items-end justify-center"
      style={{ background: 'rgba(5, 12, 24, 0.72)' }}
      role="presentation"
      onClick={saving ? undefined : onCancel}
    >
      <div
        className="w-full max-w-lg rounded-t-2xl p-4 space-y-3 max-h-[85dvh] overflow-y-auto"
        style={{ background: '#0F2340', borderTop: '1px solid var(--border-subtle)' }}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
          <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
            Этап воронки → {stageLabel}
          </p>
          <p className="text-[11px] leading-relaxed" style={{ color: 'var(--text-3)' }}>
            {hasTelegram
              ? `Сообщение уйдёт в Telegram${clientLabel ? ` ${clientLabel}` : ' клиенту'}, если галочка включена.`
              : 'Telegram ID не привязан — галочка недоступна, свяжитесь с клиентом вручную.'}
          </p>
          <label className="flex items-center gap-2 text-xs touch-manipulation min-h-[44px]">
            <input
              type="checkbox"
              checked={notifyClient && hasTelegram}
              disabled={!hasTelegram}
              onChange={(e) => setNotifyClient(e.target.checked)}
              className="w-4 h-4"
            />
            <span style={{ color: 'var(--text-2)' }}>Отправить в Telegram клиенту</span>
          </label>
          <textarea
            className="input-glass w-full text-sm min-h-[72px] rounded-xl p-3"
            placeholder="Комментарий менеджера (допишется к стандартному тексту)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          {notifyClient && hasTelegram && (
            <div
              className="rounded-xl p-3 text-[11px] leading-relaxed whitespace-pre-wrap"
              style={{ background: 'rgba(255,255,255,0.04)', color: 'var(--text-2)' }}
            >
              {preview}
            </div>
          )}
          {error && (
            <p className="text-[11px] leading-snug px-2 py-2 rounded-lg" style={{ color: '#FC814A', background: 'rgba(252,129,74,0.12)' }}>
              {error}
            </p>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={onCancel}
              className="press flex-1 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px] disabled:opacity-50"
              style={{ color: 'var(--text-3)' }}
            >
              Отмена
            </button>
            <button
              type="button"
              disabled={saving}
              onClick={() =>
                onConfirm({
                  notifyClient: notifyClient && hasTelegram,
                  clientMessage: comment.trim(),
                })
              }
              className="press flex-1 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px] flex items-center justify-center gap-2 disabled:opacity-50"
              style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : null}
              {saving ? 'Сохранение…' : 'Сохранить этап'}
            </button>
          </div>
      </div>
    </div>
  );
}
